import en from './en.js';

export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_LOCALE = en;

const SUPPORTED_LANGUAGE_CODES = ['en', 'tr', 'es', 'fr', 'de', 'it', 'id', 'th', 'bn', 'hi', 'pt'];
const SUPPORTED_LANGUAGE_SET = new Set(SUPPORTED_LANGUAGE_CODES);

// `hreflang` is language-only on purpose: every localized page serves one
// language to all of its speakers (es covers Spain and Latin America, de covers
// Austria and Switzerland, pt covers every Portuguese market), so a region-locked
// code would only match one country. `localeTag` (<html lang>, inLanguage, date
// formatting) and `ogLocale` (og:locale requires a territory) keep the region.
export const LANGUAGE_MARKETS = {
  en: {
    country: 'US',
    localeTag: 'en-US',
    hreflang: 'en',
    ogLocale: 'en_US'
  },
  tr: {
    country: 'TR',
    localeTag: 'tr-TR',
    hreflang: 'tr',
    ogLocale: 'tr_TR'
  },
  es: {
    country: 'ES',
    localeTag: 'es-ES',
    hreflang: 'es',
    ogLocale: 'es_ES'
  },
  fr: {
    country: 'FR',
    localeTag: 'fr-FR',
    hreflang: 'fr',
    ogLocale: 'fr_FR'
  },
  de: {
    country: 'DE',
    localeTag: 'de-DE',
    hreflang: 'de',
    ogLocale: 'de_DE'
  },
  it: {
    country: 'IT',
    localeTag: 'it-IT',
    hreflang: 'it',
    ogLocale: 'it_IT'
  },
  id: {
    country: 'ID',
    localeTag: 'id-ID',
    hreflang: 'id',
    ogLocale: 'id_ID'
  },
  th: {
    country: 'TH',
    localeTag: 'th-TH',
    hreflang: 'th',
    ogLocale: 'th_TH'
  },
  bn: {
    country: 'BD',
    localeTag: 'bn-BD',
    hreflang: 'bn',
    ogLocale: 'bn_BD'
  },
  hi: {
    country: 'IN',
    localeTag: 'hi-IN',
    hreflang: 'hi',
    ogLocale: 'hi_IN'
  },
  pt: {
    country: 'BR',
    localeTag: 'pt-BR',
    hreflang: 'pt',
    ogLocale: 'pt_BR'
  }
};

export const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'th', label: 'ไทย' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'pt', label: 'Português (Brasil)' },
];

export const normalizeLanguage = (value) => {
  if (!value || typeof value !== 'string') return DEFAULT_LANGUAGE;
  const normalized = value.toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGE_SET.has(normalized) ? normalized : DEFAULT_LANGUAGE;
};

export const getLanguageMarket = (value) => (
  LANGUAGE_MARKETS[normalizeLanguage(value)] || LANGUAGE_MARKETS[DEFAULT_LANGUAGE]
);

export const getLanguageDirection = (value) => (
  getLanguageMarket(value).direction || 'ltr'
);

const LOCALE_LOADERS = {
  en: async () => en,
  tr: async () => (await import('./tr.js')).default,
  es: async () => (await import('./es.js')).default,
  fr: async () => (await import('./fr.js')).default,
  de: async () => (await import('./de.js')).default,
  it: async () => (await import('./it.js')).default,
  id: async () => (await import('./id.js')).default,
  th: async () => (await import('./th.js')).default,
  bn: async () => (await import('./bn.js')).default,
  hi: async () => (await import('./hi.js')).default,
  pt: async () => (await import('./pt.js')).default
};

const PROMPTER_LOCALE_EXPORTS = {
  en: 'prompterEn',
  tr: 'prompterTr',
  es: 'prompterEs',
  fr: 'prompterFr',
  de: 'prompterDe'
};

export const loadLocale = async (value) => {
  const language = normalizeLanguage(value);
  const loader = LOCALE_LOADERS[language] || LOCALE_LOADERS[DEFAULT_LANGUAGE];
  try {
    const loadedLocale = await loader();
    return language === DEFAULT_LANGUAGE
      ? loadedLocale
      : { ...DEFAULT_LOCALE, ...loadedLocale };
  } catch (error) {
    console.warn(`Failed to load locale "${language}", falling back to default locale.`, error);
    return DEFAULT_LOCALE;
  }
};

export const loadStudioLocale = async (value) => {
  const language = normalizeLanguage(value);
  const locale = await loadLocale(language);
  const prompterExport = PROMPTER_LOCALE_EXPORTS[language];

  // Italian, Indonesian, and Thai currently carry their Prompter copy in the
  // locale snapshot itself. The shared supplement is only needed by the five
  // locales that previously made it part of every public-page preload graph.
  if (!prompterExport) return locale;

  try {
    const prompterLocales = await import('./prompter.js');
    return {
      ...locale,
      ...(prompterLocales[prompterExport] || prompterLocales.prompterEn)
    };
  } catch (error) {
    console.warn(`Failed to load Studio Prompter locale "${language}".`, error);
    return locale;
  }
};
